import { useRef, useState, useCallback } from 'react';

/**
 * Custom hook for managing toast notifications
 * 
 * @returns {Object} Toast utilities and refs
 * 
 * @example
 * const { 
 *   successAlertRef, 
 *   errorAlertRef, 
 *   showSuccessToast, 
 *   showErrorToast,
 *   ToastContainer 
 * } = useToast();
 * 
 * // Show success toast
 * showSuccessToast('Success!', 'Operation completed successfully.');
 * 
 * // Show error toast with backend error
 * try {
 *   await apiCall();
 * } catch (error) {
 *   showErrorToast(error);
 * }
 * 
 * // Render toast container in your component
 * return (
 *   <div>
 *     {/* Your component content *\/}
 *     <ToastContainer />
 *   </div>
 * );
 */
const useToast = () => {
  const successAlertRef = useRef(null);
  const errorAlertRef = useRef(null);
  const warningAlertRef = useRef(null);
  const infoAlertRef = useRef(null);
  
  const [toastMessage, setToastMessage] = useState({ 
    title: '', 
    description: '' 
  });

  /**
   * Internal function to trigger toast
   */
  const pushToast = useCallback((alert, title, description) => {
    setToastMessage({ title, description });
    setTimeout(() => {
      if (alert.current) {
        alert.current.style.transform = 'translate3d(0, 100%, 150px) scale(1)';
        alert.current.toast();
      }
    }, 100);
  }, []);

  /**
   * Extract error message from backend response
   */
  const extractErrorMessage = useCallback((error) => {
    let errorMessage = "An unexpected error occurred. Please try again.";
    let errorTitle = "Error";
    
    if (error?.response?.data) {
      const data = error.response.data;
      
      if (data.message) {
        errorMessage = data.message;
      } else if (data.error) {
        errorMessage = data.error;
      } else if (data.errors) {
        if (Array.isArray(data.errors)) {
          errorMessage = data.errors.join(', ');
        } else if (typeof data.errors === 'object') {
          errorMessage = Object.values(data.errors).flat().join(', ');
        }
      }
      
      if (data.title) {
        errorTitle = data.title;
      }
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }
    
    return { title: errorTitle, message: errorMessage };
  }, []);

  /**
   * Show success toast
   * @param {string} title - Toast title
   * @param {string} description - Toast message
   */
  const showSuccessToast = useCallback((title, description) => {
    pushToast(successAlertRef, title, description);
  }, [pushToast]);

  /**
   * Show error toast
   * Can accept either:
   * 1. Error object (will extract message from backend response)
   * 2. Title and description strings
   * 
   * @param {Error|string} errorOrTitle - Error object or title string
   * @param {string} [description] - Description (if first param is title)
   */
  const showErrorToast = useCallback((errorOrTitle, description) => {
    if (typeof errorOrTitle === 'object' && errorOrTitle !== null) {
      // It's an error object
      const { title, message } = extractErrorMessage(errorOrTitle);
      pushToast(errorAlertRef, title, message);
    } else {
      // It's a title string
      pushToast(errorAlertRef, errorOrTitle, description || '');
    }
  }, [pushToast, extractErrorMessage]);

  /**
   * Show warning toast
   * @param {string} title - Toast title
   * @param {string} description - Toast message
   */
  const showWarningToast = useCallback((title, description) => {
    pushToast(warningAlertRef, title, description);
  }, [pushToast]);

  /**
   * Show info toast
   * @param {string} title - Toast title
   * @param {string} description - Toast message
   */
  const showInfoToast = useCallback((title, description) => {
    pushToast(infoAlertRef, title, description);
  }, [pushToast]);

  return {
    // Refs for manual access if needed
    successAlertRef,
    errorAlertRef,
    warningAlertRef,
    infoAlertRef,
    toastMessage,
    
    // Helper functions
    showSuccessToast,
    showErrorToast,
    showWarningToast,
    showInfoToast,
  };
};

export default useToast;
